import { DateTime, Interval } from "luxon";

export const formatEpochTime = (
  epochTime: number,
  timeZone: string,
  showDate: boolean,
  showDateWithTomorrow: boolean
) => {
  if (!epochTime) {
    return "";
  }
  const dt = DateTime.fromMillis(epochTime).setZone(timeZone);
  const now = DateTime.now().setZone(timeZone);

  let dayLabel;

  if (showDate) {
    dayLabel = dt.toFormat("MMM d");
  } else {
    if (dt.hasSame(now, "day")) {
      dayLabel = "Today";
    } else if (dt.hasSame(now.plus({ days: 1 }), "day")) {
      dayLabel = showDateWithTomorrow
        ? `Tomorrow (${dt.toFormat("MMM d")})`
        : "Tomorrow";
    } else {
      dayLabel = dt.toFormat("MMM d");
    }
  }

  const timeLabel = dt.toFormat("h:mm a");

  return `${dayLabel} at ${timeLabel}`;
};

export const canScheduleOrder = (
  daysHours: any | undefined,
  now: DateTime,
  timezone: string,
  leadTime: number | undefined,
  isToday?: boolean
) => {
  if (!daysHours) return false;

  return daysHours
    .flatMap((hours: { start: any; end: any }) => {
      const startTime = toDateTime(hours.start, timezone);
      const endTime = toDateTime(hours.end, timezone);

      const interval = Interval.fromDateTimes(startTime, endTime);

      //If current time is already past the business hours today, cannot schedule
      if (isToday && interval.isBefore(now)) return false;
      /**
       * If Business hours start time is future time, then check if the business hours interval for today is enough
       * to prepare the order and keep it ready for schedule
       * Add lead time to start time + 15 minutes and compare the intervals
       */
      if (!isToday || interval.isAfter(now)) {
        if (interval.length("minutes") < (leadTime || 0) + 15) return false;

        return true;
      }
      //If current time is within today's business hours check if order can be prepared before closing time
      if (interval.contains(now))
        return interval.contains(
          roundToNext15Minutes(now.plus({ minutes: leadTime }))
        );

      return false;
    })
    .includes(true);
};

export const roundToNext15Minutes = (dateTime: DateTime): DateTime => {
  const minutes = dateTime.minute;
  const remainder = minutes % 15;
  if (remainder === 0) return dateTime.startOf("minute");
  return dateTime.plus({ minutes: 15 - remainder }).startOf("minute");
};

const toDateTime = (time: string, timezone?: string) =>
  DateTime.fromObject(
    {
      hour: getHour(time),
      minute: getMinutes(time),
    },
    { zone: timezone }
  );

const getHour = (time: string) => Math.floor(parseInt(time) / 100);
const getMinutes = (time: string) => parseInt(time) % 100;
